import React, { useState } from "react";
import { X, Check, CreditCard, Sparkles, Loader2, CalendarClock, Info, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { translations } from "@/i18n/translations";
import { useAuth } from "@/context/AuthContext";

export default function PricingModal({ open, onClose, packages = [], plans = [] }) {
  const [loadingId, setLoadingId] = useState(null);
  const { t, lang } = useLang();
  const { user } = useAuth();

  if (!open) return null;

  const buy = async (pkg) => {
    setLoadingId(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkg.id, origin_url: window.location.origin, method: "card" });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("pricing.err"));
      setLoadingId(null);
    }
  };

  const subscribe = async (plan) => {
    setLoadingId(plan.id);
    try {
      const { data } = await api.post("/payments/subscribe", { plan_id: plan.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("pricing.err"));
      setLoadingId(null);
    }
  };

  const activePlanId = user?.subscription?.plan_id;
  const promoText = (label) => (translations[lang]?.promoLabels || {})[label] || label || t("pricing.promo");

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-auto" onClick={onClose}>
      <div className="relative w-full max-w-4xl rounded-2xl border border-[#243047] bg-[#0A0D14] p-6 fade-up my-6"
        onClick={(e) => e.stopPropagation()} data-testid="pricing-modal">
        <button onClick={onClose} className="absolute right-4 top-4 text-[#64748B] hover:text-white" data-testid="pricing-close">
          <X className="w-5 h-5" />
        </button>

        <h2 className="font-heading text-2xl font-bold text-white">{t("pricing.title")}</h2>
        <p className="text-sm text-[#94A3B8] mt-1">{t("pricing.sub")}</p>

        {/* subscriptions */}
        <div className="mt-6" data-testid="pricing-subscriptions">
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#EC1E9C]" /> {t("pricing.subsTitle")}</h3>
          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            {plans.map((p) => {
              const isActive = activePlanId === p.id;
              return (
                <div key={p.id} data-testid={`pricing-plan-${p.daily_signals}`}
                  className={`relative rounded-xl border p-5 flex flex-col ${p.promo ? "border-[#EC1E9C] bg-gradient-to-br from-[#EC1E9C]/10 to-[#00FFC2]/5" : "border-[#243047] bg-[#121824]"}`}>
                  {p.promo && (
                    <span className="absolute -top-2.5 left-4 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] px-2 py-0.5 rounded-full">
                      <Sparkles className="w-3 h-3" /> {promoText(p.promo_label)}
                    </span>
                  )}
                  <div className="font-heading font-bold text-white">{p.name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-mono text-3xl font-extrabold text-white">${p.price}</span>
                    <span className="text-xs text-[#64748B] font-mono">{t("pricing.perWeek")}</span>
                  </div>
                  <div className="mt-1 font-mono text-sm text-[#00FFC2]"><span className="text-xl font-bold">{p.daily_signals}</span> {t("pricing.perDay")}</div>
                  <div className="text-[11px] text-[#64748B] mt-0.5">{t("pricing.autoRenew")}</div>
                  <button onClick={() => subscribe(p)} disabled={loadingId === p.id || !!activePlanId} data-testid={`subscribe-plan-${p.daily_signals}`}
                    className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold py-2.5 hover:brightness-110 transition disabled:opacity-60">
                    {loadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {isActive ? t("pricing.subscribed") : t("pricing.subscribe")}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="flex items-start gap-2 text-[11px] text-[#94A3B8] mt-3 bg-[#121824] border border-[#243047] rounded-lg px-3 py-2" data-testid="pricing-subscription-note">
            <Info className="w-3.5 h-3.5 text-[#00FFC2] mt-0.5 shrink-0" /> {t("pricing.subsNote")}
          </p>
        </div>

        {/* packages */}
        <div className="mt-7" data-testid="pricing-packages">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading font-bold text-white">{t("pricing.packsTitle")} <span className="text-xs text-[#64748B] font-normal">· {t("pricing.noExpiry")}</span></h3>
            <span className="flex items-center gap-1.5 text-xs text-[#94A3B8]" data-testid="payment-method-card"><CreditCard className="w-4 h-4 text-[#00FFC2]" /> {t("pricing.card")}</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-3">
            {packages.map((p) => (
              <div key={p.id} data-testid={`pricing-package-${p.signals}`}
                className={`relative rounded-xl border p-5 flex flex-col ${p.promo ? "border-[#EC1E9C] bg-[#EC1E9C]/5" : "border-[#243047] bg-[#121824]"}`}>
                {p.promo && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#EC1E9C] text-[#03110B] px-2 py-0.5 rounded-full">
                    <Sparkles className="w-3 h-3" /> {promoText(p.promo_label)}
                  </span>
                )}
                <div className="font-heading font-bold text-white">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1"><span className="font-mono text-3xl font-extrabold text-white">${p.price}</span></div>
                <div className="mt-1 font-mono text-sm text-[#00FFC2]">{p.signals} {t("pricing.signals")}</div>
                <div className="text-[11px] text-[#64748B] font-mono mt-0.5">${(p.price / p.signals).toFixed(2)} {t("pricing.perSignal")}</div>
                <button onClick={() => buy(p)} disabled={loadingId === p.id} data-testid={`buy-package-${p.signals}`}
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold py-2.5 hover:brightness-110 transition disabled:opacity-60">
                  {loadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {t("pricing.buy")}
                </button>
              </div>
            ))}
          </div>
        </div>
        <p className="flex items-center gap-1.5 text-[11px] text-[#64748B] mt-4" data-testid="pricing-secure-note"><ShieldCheck className="w-3.5 h-3.5 text-[#00FFC2]" /> {t("pricing.secure")}</p>
      </div>
    </div>
  );
}
